import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, type ServerError, Observable } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { createNetworkStatusNotifier } from 'react-apollo-network-status'
import { persistCache, IonicStorageWrapper } from 'apollo3-cache-persist'
import { Storage as IonicStorage } from '@ionic/storage'
import STORAGE_KEYS from '../storageKeys'
import { Storage, isSafariPWA, isNetworkConnected } from '../storage'

import { GRAPHQL_URL } from './config'
import handleCollection from './helper/handleCollection'
import { WhoamiDocument } from './types'
import { signOut } from '../../providers/Auth/hooks'

const { link, useApolloNetworkStatus: uANS } = createNetworkStatusNotifier()

export const useApolloNetworkStatus = uANS

const errorLink = onError((error) => {
  // Handle network errors (offline)
  if (error.networkError && !navigator.onLine) {
    console.log('Offline - using cached data for', error.operation?.operationName)
    // Don't show errors when offline, let Apollo use cache
    // Also prevent retries when offline
    error.networkError = null
    return
  }

  // Handle ERR_INTERNET_DISCONNECTED specifically
  if (error.networkError && (
    error.networkError.message?.includes('ERR_INTERNET_DISCONNECTED') ||
    error.networkError.message?.includes('Network request blocked while offline') ||
    error.networkError.message?.includes('Offline mode') ||
    error.networkError.message?.includes('fetch') ||
    error.networkError.message?.includes('NetworkError') ||
    error.networkError.message?.includes('Failed to fetch')
  )) {
    console.log('Network disconnected - using cached data for', error.operation?.operationName)
    // Prevent endless retries and let Apollo use cache
    error.networkError = null
    return
  }

  // Check if this might be a network timeout or connectivity issue
  if (error.networkError && !navigator.onLine) {
    console.log('Network error while offline - ignoring to prevent logout')
    error.networkError = null
    return
  }

  // eslint-disable-next-line no-console
  console.log({ error })

  if (window.location.href.includes('/s/') || window.location.href.includes('/login')) {
    return
  }

  console.log(error.graphQLErrors)

  error.graphQLErrors?.forEach(async e => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (e.extensions?.response?.statusCode === 401) {
      // For Safari PWA, use enhanced network check and longer delay
      if (isSafariPWA()) {
        const isConnected = await isNetworkConnected()
        if (isConnected) {
          // Wait longer for Safari PWA to stabilize
          setTimeout(() => {
            console.log('Received 401 while online (Safari PWA confirmed) - signing out')
            cache.writeQuery({
              query: WhoamiDocument,
              data: { whoami: null }
            })
            signOut()
          }, 3000)
        } else {
          console.log('Received 401 while offline (Safari PWA) - ignoring to prevent logout')
        }
      } else {
        // Standard behavior for other browsers
        if (navigator.onLine) {
          console.log('Received 401 while online - signing out')
          cache.writeQuery({
            query: WhoamiDocument,
            data: { whoami: null }
          })
          signOut()
        } else {
          console.log('Received 401 while offline - ignoring to prevent logout')
        }
      }
    }
  })

  if ((error.networkError as ServerError)?.statusCode === 401) {
    // Handle Safari PWA differently
    if (isSafariPWA()) {
      isNetworkConnected().then(isConnected => {
        if (isConnected) {
          // Wait longer for Safari PWA to stabilize after login
          setTimeout(async () => {
            const stillConnected = await isNetworkConnected()
            if (stillConnected) {
              console.log('Received network 401 while online (Safari PWA confirmed) - signing out')
              cache.writeQuery({
                query: WhoamiDocument,
                data: { whoami: null }
              })
              signOut()
            } else {
              console.log('Received network 401 but went offline (Safari PWA) - ignoring')
            }
          }, 3000)
        } else {
          console.log('Received network 401 while offline (Safari PWA) - ignoring to prevent logout')
        }
      })
    } else {
      // Standard behavior for other browsers
      if (navigator.onLine) {
        // Double-check after a small delay to ensure we're really online
        setTimeout(() => {
          if (navigator.onLine) {
            console.log('Received network 401 while online (confirmed) - signing out')
            cache.writeQuery({
              query: WhoamiDocument,
              data: { whoami: null }
            })
            signOut()
          } else {
            console.log('Received network 401 but went offline - ignoring')
          }
        }, 1000)
      } else {
        console.log('Received network 401 while offline - ignoring to prevent logout')
      }
    }
  }
})

const authLink = setContext(async (req, { headers }) => {
  const token = await Storage.get({ key: STORAGE_KEYS.API.LOGIN_TOKEN })
  const additionalHeaders: any = {}
  if (token?.value) {
    additionalHeaders.authorization = `Bearer ${token.value}`
  }
  return {
    headers: {
      ...headers,
      ...additionalHeaders
    }
  }
})

// Network-aware HttpLink that handles offline scenarios
const networkAwareLink = ApolloLink.from([
  new ApolloLink((operation, forward) => {
    // Check if we're offline
    if (!navigator.onLine) {
      console.log('Offline: Handling GraphQL request for', operation.operationName)

      // Check if this is a mutation that should have optimistic updates
      if (operation.query.definitions.some((def: any) => def.operation === 'mutation')) {
        const mutationDef = operation.query.definitions.find((def: any) => def.operation === 'mutation') as any
        const mutationName = mutationDef?.selectionSet?.selections?.[0]?.name?.value

        // Only handle specific mutations that need optimistic updates
        // Let voting mutations and group participant mutations be handled by proper offline systems
        const optimisticMutations = ['createGroup']

        if (optimisticMutations.includes(mutationName)) {
          console.log('Offline: Allowing optimistic mutation for', mutationName)
          return new Observable(observer => {
            observer.next({
              data: { [mutationName]: null }, // Minimal successful response
              extensions: { offline: true }
            })
            observer.complete()
          })
        } else {
          console.log('Offline: Blocking non-optimistic mutation for', mutationName)
          // For other mutations (like voting), return a network error to let other systems handle them
          return new Observable(observer => {
            observer.error(new Error(`Offline: ${mutationName} mutation blocked`))
          })
        }
      }

      // For queries when offline, don't forward to network - return cache miss to trigger cache-only
      console.log('Offline: Blocking network request for query', operation.operationName, 'to force cache-only behavior')
      return new Observable(observer => {
        // Return a network error that will be handled by the error link
        // This allows Apollo to fall back to cache-only behavior
        observer.error(new Error('ERR_INTERNET_DISCONNECTED: Offline mode'))
      })
    }

    // Continue with the request if online
    return forward(operation)
  }),
  new HttpLink({
    uri: GRAPHQL_URL
  })
])

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        activities: handleCollection(),
        playlists: handleCollection(),
        playlistTalents: handleCollection(),
        networks: handleCollection(),
        listMyTalents: handleCollection(),
        similarNetwork: handleCollection(),
        networkUsers: handleCollection(),
        networkTalents: handleCollection()
      }
    },
    Camp: {
      keyFields: ['id']
    },
    Group: {
      keyFields: ['id']
    },
    Participant: {
      keyFields: ['id']
    },
    UserMetaData: {
      keyFields: ['username']
    },
    Talent: {
      fields: {
        dataViews: {
          merge (existing, incoming) {
            if (existing?.length > incoming?.length) {
              return existing
            }
            return incoming
          }
        },
        publicRating: {
          merge (existing, incoming) {
            if (incoming.length === 0) {
              return existing
            }
            return incoming
          }
        },
        rateCount: {
          merge (existing, incoming) {
            if (!incoming) {
              return existing
            }
            return incoming
          }
        }
      }
    },
    Network: {
      fields: {
        networkRoles: {
          merge (existing, incoming) {
            if (!incoming || incoming.length === 0 || existing?.length > incoming?.length) {
              return existing
            }
            return incoming
          }
        }
      }
    }
  }
})

// Set up your client
const apolloClient = async () => {
  // Enable cache persistence for all platforms (web and native)
  const store = new IonicStorage()
  await store.create()
  await persistCache({
    cache,
    storage: new IonicStorageWrapper(store),
    maxSize: false // Disable cache size limit
  })

  const client = new ApolloClient({
    cache,
    link: ApolloLink.from([
      errorLink,
      link,
      authLink,
      // restLink,
      networkAwareLink
    ]),
    uri: GRAPHQL_URL,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: navigator.onLine ? 'cache-and-network' : 'cache-only',
        errorPolicy: 'all',
        returnPartialData: true,
        notifyOnNetworkStatusChange: false
      },
      query: {
        fetchPolicy: navigator.onLine ? 'cache-first' : 'cache-only',
        errorPolicy: 'all',
        returnPartialData: true
      }
    }
  })

  // Listen for network changes to update fetch policy
  window.addEventListener('online', () => {
    console.log('Network: Back online - updating default fetch policies')

    // Update default options for new queries
    if (client.defaultOptions.watchQuery) {
      client.defaultOptions.watchQuery.fetchPolicy = 'cache-and-network'
    }
    if (client.defaultOptions.query) {
      client.defaultOptions.query.fetchPolicy = 'cache-first'
    }

    // Refetch active queries with fresh data
    client.refetchQueries({
      include: 'active'
    })
  })

  window.addEventListener('offline', () => {
    console.log('Network: Gone offline - switching to cache-only mode')

    // Update default options for offline mode
    if (client.defaultOptions.watchQuery) {
      client.defaultOptions.watchQuery.fetchPolicy = 'cache-only'
    }
    if (client.defaultOptions.query) {
      client.defaultOptions.query.fetchPolicy = 'cache-only'
    }
  })

  return client
}

export default apolloClient
